import React, { useContext, useEffect, useState } from 'react';
import { AuthContext } from '../contexts/AuthContext';
import { BusinessStatusContext } from '../contexts/BusinessStatusContext';
import {
    createBusinessClosure,
    deleteBusinessClosure,
    fetchBusinessClosures,
    updateBusinessClosure,
} from '../services/businessStatus';
import {
    formatClosureDateTime,
    formatDateInputValue,
    formatTimeInputValue,
    SOUTH_AFRICA_TIME_ZONE,
} from '../utils/businessHours';
import './BusinessClosuresPage.css';

function createInitialFormState() {
    const now = new Date();
    const twoHoursFromNow = new Date(now.getTime() + (2 * 60 * 60 * 1000));

    return {
        startDate: formatDateInputValue(now),
        endDate: formatDateInputValue(now),
        startTime: formatTimeInputValue(now),
        endTime: formatTimeInputValue(twoHoursFromNow),
        allDay: false,
        isActive: true,
        message: '',
    };
}

function buildPayloadFromForm(formState) {
    const startTime = formState.allDay ? '00:00' : formState.startTime;
    const endTime = formState.allDay ? '23:59' : formState.endTime;
    const startAt = new Date(`${formState.startDate}T${startTime}:00`);
    const endAt = new Date(`${formState.endDate}T${endTime}:00`);

    return {
        startAt,
        endAt,
        message: formState.message.trim(),
        allDay: formState.allDay,
        isActive: formState.isActive,
    };
}

function buildFormFromClosure(closure) {
    return {
        startDate: formatDateInputValue(closure.startAt),
        endDate: formatDateInputValue(closure.endAt),
        startTime: formatTimeInputValue(closure.startAt),
        endTime: formatTimeInputValue(closure.endAt),
        allDay: Boolean(closure.allDay),
        isActive: Boolean(closure.isActive),
        message: closure.message || '',
    };
}

function getClosureStatusLabel(closure) {
    const now = new Date();
    const startAt = new Date(closure.startAt);
    const endAt = new Date(closure.endAt);

    if (!closure.isActive) {
        return 'Inactive';
    }

    if (startAt <= now && endAt > now) {
        return 'Active now';
    }

    if (startAt > now) {
        return 'Upcoming';
    }

    return 'Past';
}

function BusinessClosuresPage() {
    const { user } = useContext(AuthContext);
    const { businessStatus, refreshBusinessStatus } = useContext(BusinessStatusContext);
    const [closures, setClosures] = useState([]);
    const [formState, setFormState] = useState(createInitialFormState());
    const [editingClosureId, setEditingClosureId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [pendingDelete, setPendingDelete] = useState(null);

    const loadClosures = async () => {
        setLoading(true);
        try {
            const data = await fetchBusinessClosures();
            setClosures(data);
            setError('');
        } catch (err) {
            setError(err.message || 'Failed to load closure periods.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadClosures();
    }, []);

    const resetForm = () => {
        setEditingClosureId(null);
        setFormState(createInitialFormState());
    };

    const handleInputChange = (event) => {
        const { name, type, checked, value } = event.target;
        setFormState((currentState) => ({
            ...currentState,
            [name]: type === 'checkbox' ? checked : value,
        }));
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        const payload = buildPayloadFromForm(formState);

        if (Number.isNaN(payload.startAt.getTime()) || Number.isNaN(payload.endAt.getTime())) {
            setError('Please choose a valid start and end date/time.');
            return;
        }

        if (payload.startAt >= payload.endAt) {
            setError('The closure end time must be after the start time.');
            return;
        }

        if (!payload.message) {
            setError('Please add the banner message customers should see.');
            return;
        }

        setSaving(true);

        try {
            const requestPayload = {
                ...payload,
                startAt: payload.startAt.toISOString(),
                endAt: payload.endAt.toISOString(),
            };

            if (editingClosureId) {
                await updateBusinessClosure(editingClosureId, requestPayload);
            } else {
                await createBusinessClosure(requestPayload);
            }

            await Promise.all([loadClosures(), refreshBusinessStatus()]);
            resetForm();
            setError('');
        } catch (err) {
            setError(err.message || 'Failed to save the closure period.');
        } finally {
            setSaving(false);
        }
    };

    const handleEdit = (closure) => {
        setEditingClosureId(closure.id);
        setFormState(buildFormFromClosure(closure));
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleDelete = async (closureId) => {
        try {
            await deleteBusinessClosure(closureId);
            await Promise.all([loadClosures(), refreshBusinessStatus()]);
            setPendingDelete(null);
        } catch (err) {
            setError(err.message || 'Failed to delete the closure period.');
        }
    };

    if (!user || !['admin', 'superadmin'].includes(user.role)) {
        return (
            <div className="business-closures-page">
                <div className="business-closures-card">
                    <h2>Business Closures</h2>
                    <p>You need an admin account to manage closure periods.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="business-closures-page">
            <div className="business-closures-card business-closures-hero">
                <div>
                    <p className="business-closures-kicker">Store status</p>
                    <h2>Business Closures</h2>
                    <p className="business-closures-summary">
                        Use this page to schedule full-day or partial-day closures and control the message shown on the landing-page banner.
                    </p>
                </div>
                <div className={`business-status-pill ${businessStatus.canPlaceOrders ? 'open' : 'closed'}`}>
                    {businessStatus.canPlaceOrders ? 'Accepting orders' : 'Closed to orders'}
                </div>
            </div>

            <div className="business-closures-card business-closures-live-banner">
                <h3>Current customer banner</h3>
                <p>{businessStatus.banner?.message}</p>
                <p className="business-closures-timezone">Timezone: {SOUTH_AFRICA_TIME_ZONE}</p>
            </div>

            {pendingDelete && (
                <div className="business-closures-card business-closures-error" role="alert">
                    <span>Delete the closure period for {pendingDelete.label}? This cannot be undone.</span>
                    <div className="closure-card-actions">
                        <button type="button" className="secondary-button" onClick={() => setPendingDelete(null)}>Cancel</button>
                        <button type="button" className="danger-button" onClick={() => handleDelete(pendingDelete.id)}>Confirm delete</button>
                    </div>
                </div>
            )}

            <div className="business-closures-card">
                <div className="business-closures-form-header">
                    <h3>{editingClosureId ? 'Edit closure period' : 'Create closure period'}</h3>
                    {editingClosureId && (
                        <button type="button" className="secondary-button" onClick={resetForm}>
                            Cancel edit
                        </button>
                    )}
                </div>

                <form className="business-closures-form" onSubmit={handleSubmit}>
                    <label>
                        Start date
                        <input
                            type="date"
                            name="startDate"
                            value={formState.startDate}
                            onChange={handleInputChange}
                            required
                        />
                    </label>

                    <label>
                        End date
                        <input
                            type="date"
                            name="endDate"
                            value={formState.endDate}
                            onChange={handleInputChange}
                            required
                        />
                    </label>

                    {!formState.allDay && (
                        <>
                            <label>
                                Start time
                                <input
                                    type="time"
                                    name="startTime"
                                    value={formState.startTime}
                                    onChange={handleInputChange}
                                    required={!formState.allDay}
                                />
                            </label>

                            <label>
                                End time
                                <input
                                    type="time"
                                    name="endTime"
                                    value={formState.endTime}
                                    onChange={handleInputChange}
                                    required={!formState.allDay}
                                />
                            </label>
                        </>
                    )}

                    <label className="checkbox-row">
                        <input
                            type="checkbox"
                            name="allDay"
                            checked={formState.allDay}
                            onChange={handleInputChange}
                        />
                        All-day closure
                    </label>

                    <label className="checkbox-row">
                        <input
                            type="checkbox"
                            name="isActive"
                            checked={formState.isActive}
                            onChange={handleInputChange}
                        />
                        Active
                    </label>

                    <label className="message-field">
                        Banner message
                        <textarea
                            name="message"
                            rows="4"
                            value={formState.message}
                            onChange={handleInputChange}
                            placeholder="Example: We are closing early today for a private function. Orders reopen tomorrow at 7 AM."
                            required
                        />
                    </label>

                    {error && <p className="business-closures-error">{error}</p>}

                    <div className="business-closures-actions">
                        <button type="submit" className="primary-button" disabled={saving}>
                            {saving ? 'Saving...' : editingClosureId ? 'Update closure' : 'Save closure'}
                        </button>
                        <button type="button" className="secondary-button" onClick={resetForm} disabled={saving}>
                            Reset form
                        </button>
                    </div>
                </form>
            </div>

            <div className="business-closures-card">
                <div className="business-closures-form-header">
                    <h3>Scheduled closures</h3>
                    <button type="button" className="secondary-button" onClick={loadClosures} disabled={loading}>
                        Refresh
                    </button>
                </div>

                {loading ? (
                    <p>Loading closures...</p>
                ) : closures.length === 0 ? (
                    <p>No closure periods have been scheduled yet.</p>
                ) : (
                    <div className="closure-list">
                        {closures.map((closure) => (
                            <div className="closure-card" key={closure.id}>
                                <div className="closure-card-header">
                                    <span className={`closure-status ${getClosureStatusLabel(closure).toLowerCase().replace(/\s+/g, '-')}`}>
                                        {getClosureStatusLabel(closure)}
                                    </span>
                                    <span className="closure-id">#{closure.id}</span>
                                </div>
                                <p className="closure-window">
                                    {formatClosureDateTime(closure.startAt)} to {formatClosureDateTime(closure.endAt)}
                                </p>
                                <p className="closure-message">{closure.message}</p>
                                <div className="closure-card-actions">
                                    <button type="button" className="secondary-button" onClick={() => handleEdit(closure)}>
                                        Edit
                                    </button>
                                    <button type="button" className="danger-button" onClick={() => setPendingDelete({ id: closure.id, label: formatClosureDateTime(closure.startAt) })}>
                                        Delete
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

export default BusinessClosuresPage;
